/**
 * The fog-of-war wash over the watercolor atlas (worldMapFog.ts decides what is
 * charted). A cloudy parchment wash covers the 1600x1000 artwork frame; each
 * charted place clears a soft-edged window, each rumoured place a fainter one.
 */
import React from 'react';
import type { WorldMapFog } from './worldMapFog';

interface FogAnchor { id: string; x: number; y: number }

const CHARTED_RADIUS = 115;
const RUMOURED_RADIUS = 60;

export const WatercolorFogLayer: React.FC<{ fog: WorldMapFog; anchors: readonly FogAnchor[] }> = ({ fog, anchors }) => (
  <g className="watercolor-map-fog" data-world-map-fog={fog.policyId} aria-hidden="true">
    <defs>
      {/* Mask: white keeps the fog, black clears it; the gradient feathers each window's edge. */}
      <radialGradient id="world-map-fog-window">
        <stop offset="0" stopColor="black" />
        <stop offset="0.55" stopColor="black" />
        <stop offset="1" stopColor="white" />
      </radialGradient>
      <filter id="world-map-fog-clouds" x="0" y="0" width="1" height="1">
        <feTurbulence type="fractalNoise" baseFrequency="0.006 0.011" numOctaves={4} seed={11} />
        <feColorMatrix type="matrix" values="0 0 0 0 0.42  0 0 0 0 0.37  0 0 0 0 0.30  0 0 0 -1.4 1.05" />
      </filter>
      <mask id="world-map-fog-mask" maskUnits="userSpaceOnUse" x={0} y={0} width={1600} height={1000}>
        <rect width={1600} height={1000} fill="white" />
        {anchors.filter(anchor => fog.charted.has(anchor.id)).map(anchor => (
          <circle key={anchor.id} cx={anchor.x} cy={anchor.y} r={CHARTED_RADIUS} fill="url(#world-map-fog-window)" />
        ))}
        {anchors.filter(anchor => fog.rumoured.has(anchor.id)).map(anchor => (
          <circle key={anchor.id} cx={anchor.x} cy={anchor.y} r={RUMOURED_RADIUS} fill="url(#world-map-fog-window)" opacity={0.55} />
        ))}
      </mask>
    </defs>
    <g mask="url(#world-map-fog-mask)">
      <rect className="watercolor-map-fog-wash" width={1600} height={1000} />
      <rect width={1600} height={1000} filter="url(#world-map-fog-clouds)" />
    </g>
  </g>
);
